import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { alpha, Box, CircularProgress, Dialog, IconButton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ViewInArRoundedIcon from "@mui/icons-material/ViewInArRounded";
import { GetSlideByIdCall, GetTitlesCallNoStore } from "../../../redux/api/title.api";
import { basicURL } from "../../../apiManager/APIConfig";

const ACCENT = "#0061FE";

function absoluteFileUrl(raw) {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const trimmed = raw.trim();
  if (/^https?:\/\//.test(trimmed)) return trimmed;
  const base = (basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  return trimmed.startsWith("/") ? base + trimmed : `${base}/${trimmed}`;
}

/** Older API builds don't return first_slide_id; find it in the catalogue tree instead. */
async function resolveFirstSlideId(row, lang) {
  if (row?.first_slide_id) return row.first_slide_id;
  const targetId = String(row?.counterpart_title_id || "");
  if (!targetId || !row?.related_type) return null;
  const list = await GetTitlesCallNoStore(lang, row.related_type);
  for (const title of Array.isArray(list) ? list : []) {
    if (String(title?.id) === targetId) {
      return title?.subtitles?.[0]?.slides?.[0]?.id || title?.slides?.[0]?.id || null;
    }
    const sub = (title?.subtitles || []).find((s) => String(s?.id) === targetId);
    if (sub) return sub?.slides?.[0]?.id || null;
  }
  return null;
}

/**
 * Full-screen glass overlay that previews a related 3D model in place,
 * with prev/next between the related models and a link to the full page.
 */
export default function ModelViewerOverlay({ rows = [], index = null, onIndexChange, onClose, lang = "en" }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const open = index != null && rows[index] != null;
  const row = open ? rows[index] : null;

  const [slideId, setSlideId] = useState(null);
  const [modelUrl, setModelUrl] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [frameLoaded, setFrameLoaded] = useState(false);

  useEffect(() => {
    if (!row) return undefined;
    let alive = true;
    setStatus("loading");
    setFrameLoaded(false);
    setModelUrl(null);
    setSlideId(null);
    (async () => {
      try {
        const sid = await resolveFirstSlideId(row, lang);
        if (!alive) return;
        if (!sid) {
          setStatus("error");
          return;
        }
        setSlideId(sid);
        const res = await GetSlideByIdCall({ id: sid, lang });
        if (!alive) return;
        const slide = res?.data ?? res;
        const files = slide?.Files ?? slide?.files ?? [];
        const url = absoluteFileUrl(files?.[0]?.url ?? files?.[0]?.path);
        if (!url) {
          setStatus("error");
          return;
        }
        setModelUrl(url);
        setStatus("ready");
      } catch (e) {
        if (alive) setStatus("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [row?.id, lang]);

  const hasMany = rows.length > 1;
  const step = (delta) => {
    if (!hasMany) return;
    onIndexChange?.((index + delta + rows.length) % rows.length);
  };

  useEffect(() => {
    if (!open || !hasMany) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, hasMany, index, rows.length]);

  const iframeSrc = modelUrl
    ? `${window.location.origin}/model-viewer.html?src=${encodeURIComponent(modelUrl)}&theme=dark`
    : "";

  const glassBtn = {
    width: 40,
    height: 40,
    color: "#fff",
    bgcolor: "rgba(255,255,255,0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,0.14)",
    "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(2,6,23,0.6)", backdropFilter: "blur(8px)" },
        },
      }}
      PaperProps={{
        sx: {
          width: fullScreen ? "100%" : "min(1200px, 94vw)",
          height: fullScreen ? "100%" : "min(820px, 90vh)",
          m: 0,
          borderRadius: fullScreen ? 0 : 4,
          overflow: "hidden",
          position: "relative",
          bgcolor: "#0b1020",
          backgroundImage: "radial-gradient(ellipse at 50% 35%, #1e293b 0%, #0b1020 70%)",
          boxShadow: "0 40px 120px rgba(0,0,0,0.55)",
        },
      }}
    >
      {/* Viewer */}
      <Box sx={{ position: "absolute", inset: 0 }}>
        {status === "ready" ? (
          <iframe
            key={iframeSrc}
            title={row?.title || "3D model"}
            src={iframeSrc}
            onLoad={() => setFrameLoaded(true)}
            style={{ width: "100%", height: "100%", border: 0, display: "block" }}
            allow="fullscreen; xr-spatial-tracking"
          />
        ) : null}
        {(status === "loading" || (status === "ready" && !frameLoaded)) && (
          <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
            <Box sx={{ textAlign: "center", color: "rgba(255,255,255,0.75)" }}>
              <CircularProgress size={38} sx={{ color: "#fff" }} />
              <Typography sx={{ mt: 1.5, fontSize: 13.5, color: "inherit" }}>Loading model…</Typography>
            </Box>
          </Box>
        )}
        {status === "error" && (
          <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", px: 3 }}>
            <Box sx={{ textAlign: "center", color: "rgba(255,255,255,0.8)", maxWidth: 360 }}>
              <ViewInArRoundedIcon sx={{ fontSize: 44, opacity: 0.6 }} />
              <Typography sx={{ mt: 1, fontWeight: 700, color: "#fff" }}>This model can&apos;t be previewed</Typography>
              <Typography sx={{ mt: 0.5, fontSize: 13.5, color: "inherit" }}>
                No 3D file is attached to it yet.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Top bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: { xs: 1.5, sm: 2 },
          background: "linear-gradient(to bottom, rgba(2,6,23,0.75), rgba(2,6,23,0))",
          pointerEvents: "none",
          "& > *": { pointerEvents: "auto" },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            color: "#fff",
            bgcolor: alpha(ACCENT, 0.85),
          }}
        >
          <ViewInArRoundedIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{ color: "#fff", fontWeight: 700, fontSize: 16, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {row?.title || "3D model"}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12.5 }}>
            Related 3D model{hasMany ? ` · ${index + 1} of ${rows.length}` : ""}
          </Typography>
        </Box>
        {slideId ? (
          <Tooltip title="Open full page">
            <IconButton
              aria-label="Open full page"
              onClick={() => {
                onClose?.();
                navigate(`/showThreeD/${slideId}/${lang}`);
              }}
              sx={glassBtn}
            >
              <OpenInNewRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <Tooltip title="Close (Esc)">
          <IconButton aria-label="Close" onClick={onClose} sx={glassBtn}>
            <CloseRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Prev / next */}
      {hasMany
        ? [
            { side: "left", delta: -1, Icon: ChevronLeftRoundedIcon, label: "Previous model" },
            { side: "right", delta: 1, Icon: ChevronRightRoundedIcon, label: "Next model" },
          ].map(({ side, delta, Icon, label }) => (
            <IconButton
              key={side}
              aria-label={label}
              onClick={() => step(delta)}
              sx={{
                ...glassBtn,
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                [side]: { xs: 8, sm: 16 },
                width: 44,
                height: 44,
              }}
            >
              <Icon />
            </IconButton>
          ))
        : null}

      {/* Hint */}
      {status === "ready" && frameLoaded ? (
        <Typography
          sx={{
            position: "absolute",
            bottom: 14,
            left: "50%",
            transform: "translateX(-50%)",
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            fontSize: 12,
            color: "rgba(255,255,255,0.75)",
            bgcolor: "rgba(2,6,23,0.45)",
            backdropFilter: "blur(8px)",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          Drag to rotate · Scroll to zoom
        </Typography>
      ) : null}
    </Dialog>
  );
}
